import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronRight, ChevronDown } from "lucide-react";
import { site } from "@/config";
import { EASE } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";

const HEADLINES = ["Get discovered.", "Get cited.", "Get recommended."];

export default function Hero() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ li: 0, ci: 0 });
  const [activeLine, setActiveLine] = useState(0);

  useEffect(() => {
    let li = 0;
    let ci = 0;
    let t;
    let cancelled = false;
    const tick = () => {
      if (cancelled || li >= HEADLINES.length) return;
      ci += 1;
      setActiveLine(li);
      const snapLi = li;
      const snapCi = ci;
      setPos((prev) =>
        snapLi > prev.li || (snapLi === prev.li && snapCi > prev.ci)
          ? { li: snapLi, ci: snapCi }
          : prev
      );
      if (ci < HEADLINES[li].length) {
        t = setTimeout(tick, 55 + Math.random() * 40);
      } else {
        li += 1;
        ci = 0;
        t = setTimeout(tick, 340);
      }
    };
    t = setTimeout(tick, 900);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  const scrollToWork = (e) => {
    e.preventDefault();
    window.__lenis?.scrollTo("#work", { offset: -72 });
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden text-center"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" data-testid="hero-background">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[85vw] h-[85vw] rounded-full bg-[#0071e3]/[0.09] blur-3xl animate-aurora" />
        <div className="absolute top-1/4 -left-72 w-[55vw] h-[55vw] rounded-full bg-[#c644fc]/[0.06] blur-3xl animate-aurora-slow" />
        <div className="absolute bottom-[-20%] -right-72 w-[50vw] h-[50vw] rounded-full bg-[#00c2ff]/[0.08] blur-3xl animate-aurora" />
        <ParticleField className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 px-6 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="inline-flex items-center gap-2.5 border border-black/10 rounded-full px-5 py-2 mb-10 bg-white/60 backdrop-blur"
          data-testid="hero-role-label"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-dot" />
          <span className="font-mono text-[10px] sm:text-xs tracking-[0.16em] sm:tracking-[0.22em] uppercase text-muted-foreground">
            {site.name} · {site.role}
          </span>
        </motion.div>

        <h1
          className="font-display font-semibold text-[13vw] sm:text-7xl lg:text-8xl leading-[1.03] tracking-[-0.035em]"
          data-testid="hero-headline"
        >
          {HEADLINES.map((line, i) => {
            const text =
              i < pos.li ? line : i === pos.li ? line.slice(0, pos.ci) : "";
            return (
              <span key={line} className="block min-h-[1.05em]">
                <span className={i === 2 ? "text-gradient" : ""}>{text}</span>
                {activeLine === i && (
                  <span className="inline-block w-[0.45ch] h-[0.8em] bg-primary align-[-0.08em] ml-1.5 animate-pulse-dot" />
                )}
              </span>
            );
          })}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 4.4 }}
          className="mt-8 max-w-xl mx-auto text-base sm:text-xl text-muted-foreground leading-relaxed"
          data-testid="hero-tagline"
        >
          {site.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE, delay: 4.7 }}
          className="mt-6 font-mono text-[11px] sm:text-xs tracking-[0.22em] uppercase text-muted-foreground"
        >
          SEO for search engines · Visibility for LLMs
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 4.9 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-6"
        >
          <a
            href={site.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-call-btn"
            className="group inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-4 text-sm sm:text-base font-medium hover:scale-[1.05] hover:shadow-[0_8px_30px_rgba(0,113,227,0.35)] transition-[transform,box-shadow] duration-300"
          >
            Book a discovery call
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#work"
            onClick={scrollToWork}
            data-testid="hero-see-work-btn"
            className="group inline-flex items-center gap-1 text-primary text-sm sm:text-base font-medium link-sweep"
          >
            See the work
            <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 5.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <ChevronDown className="w-5 h-5 text-muted-foreground animate-float" />
      </motion.div>
    </section>
  );
}
